import React, { useState } from "react";
import DropZone from "../Dropzone";
import toast from "../../../utils/toast";
import addPhoto from "../../../Assets/Icons/imgIcon.svg";
import AddPhoto from "./AddPhoto";
import { uploadFile } from "../../../utils/s3";

const UploadDocument = ({
  thumbnail,
  name,
  setFieldValue,
  error,
  touched,
  iconText,
  parentDivClass,
  setS3Url,
  logoFileImage,
  setLogoFileImage,
  // Optional controls
  autoCompress = false,
  compressThresholdBytes = 10485760, // 10MB
  dropzoneMaxSize = 10485760, // default 10MB
  onLoadingChange, // optional callback to bubble loading state up
  onFileSelected, // Optional: if provided, this function is called with the file instead of internal upload logic
  previewImageClass, // Optional class for the preview image
  showChangeText = true, // Control visibility of "Change Thumbnail" text
  removeBtnType = "default", // Control remove button style
  removeBorderOnPreview = false, // Control border visibility on preview
}) => {
  const [loading, setLoading] = useState(false);

  const getBase64 = (file, cb) => {
    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function () {
      cb(reader.result);
    };
    reader.onerror = function (error) {
      console.log("Error: ", error);
    };
  };

  const validateImage = (data) => {
    var idxDot = data.lastIndexOf(".") + 1;
    var extFile = data.substr(idxDot, data.length).toLowerCase();
    if (extFile === "jpg" || extFile === "jpeg" || extFile === "png") {
      return true;
    } else {
      return false;
    }
  };

  // Compress image to stay under threshold using canvas; converts PNG to JPEG for better results
  const compressImageIfNeeded = (file) => {
    return new Promise((resolve) => {
      if (!autoCompress || file.size <= compressThresholdBytes) {
        resolve({ file, fileName: file.name });
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = async () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          const maxWidth = 1600; // good balance for thumbnails
          const maxHeight = 1600;
          let { width, height } = img;
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          const targetMime = "image/jpeg"; // ensures quality param works
          let quality = 0.85;

          const toBlobWithQuality = (q) =>
            new Promise((res) => canvas.toBlob((b) => res(b), targetMime, q));

          let blob = await toBlobWithQuality(quality);
          // Reduce quality iteratively until under threshold or minimum quality reached
          while (blob && blob.size > compressThresholdBytes && quality > 0.5) {
            quality -= 0.1;
            blob = await toBlobWithQuality(quality);
          }

          const newName = file.name.replace(/\.(png|jpeg|jpg)$/i, ".jpg");
          const compressedFile = new File([blob], newName, { type: targetMime });
          resolve({ file: compressedFile, fileName: newName });
        };
        img.onerror = () => resolve({ file, fileName: file.name });
        img.src = reader.result;
      };
      reader.onerror = () => resolve({ file, fileName: file.name });
      reader.readAsDataURL(file);
    });
  };

  const changeHandler = async (files) => {
    let incomingFile = files[0];
    let fileName = incomingFile.name;
    let checkValidImage = validateImage(fileName);

    if (checkValidImage) {
      // If parent provided a custom handler (e.g. for cropping), delegate to it
      if (onFileSelected) {
        onFileSelected(incomingFile);
        return;
      }

      setFieldValue(name, fileName);
      setLoading(true);
      if (typeof onLoadingChange === "function") onLoadingChange(true);
      const { file: fileToUpload, fileName: finalName } = await compressImageIfNeeded(incomingFile);
      const s3Url = await uploadFile(fileToUpload, finalName);
      if (s3Url) {
        setS3Url(s3Url);
        setLoading(false);
        if (typeof onLoadingChange === "function") onLoadingChange(false);
      }
      let logoImageUrl = URL.createObjectURL(fileToUpload);
      setLogoFileImage(logoImageUrl);
      setFieldValue(name, finalName);
      // Trigger read to ensure browser releases object URL memory for large files
      getBase64(fileToUpload, () => { });
    } else {
      toast.error("Only JPG, JPEG and PNG files are allowed!");
      handleRejectedFiles(files);
    }
  };

  const handleRejectedFiles = (files) => {
    files[0]?.errors?.forEach((error) => {
      let message = error.message;
      if (error.code === "file-too-large") {
        const sizeInMb = (dropzoneMaxSize / (1024 * 1024)).toFixed(0);
        message = `File is larger than ${sizeInMb}MB`;
      }
      toast.error(message);
    });
  };

  const clearLogoImage = () => {
    setFieldValue(name, null);
    setS3Url();
    setLogoFileImage();
    setLoading(false);
    if (typeof onLoadingChange === "function") onLoadingChange(false);
  };
  return (
    <div
      className={`${parentDivClass} mx-auto rounded-[20px] p-6 flex flex-col items-center justify-center`}
      style={{
        border: (removeBorderOnPreview && logoFileImage) ? "none" : "2px dashed #00ADEF",
        backgroundColor: "#FFFFFF",
      }}
    >
      {!logoFileImage ? (
        <DropZone
          acceptedFiles={changeHandler}
          rejectedFiles={handleRejectedFiles}
          maxLength={1}
          supportText={""}
          multiple={false}
          maxSize={dropzoneMaxSize}
          accept="image/png, image/jpeg"
        >
          <div className="flex flex-col items-center justify-center w-full h-full gap-4 cursor-pointer">
            {/* Inner White Card Trigger */}
            <div className="w-[300px] h-[80px] bg-white border border-[#E0E0E0] rounded-[10px] flex flex-col items-center justify-center gap-2">
              {loading ? (
                <span className="text-[#00ADEF]">Loading...</span>
              ) : (
                <>
                  <img src={addPhoto} className="w-[20px] h-[20px]" alt="upload" />
                  <span className="text-[14px] font-[500] text-[#767676]">{iconText || "Upload Thumbnail"}</span>
                </>
              )}
            </div>

            {/* Bottom Text */}
            <div className="flex flex-col items-center justify-center gap-1">
              <span className="text-[16px] font-[500] text-[#767676] font-poppins">
                Drag & Drop
              </span>
              <span className="text-[14px] font-[400] text-[#767676] font-poppins">
                Your files here Or{" "}
                <span className="text-[#00ADEF] font-[500]">
                  Browse
                </span> to upload
              </span>
            </div>
          </div>
        </DropZone>
      ) : (
        <div className="w-full h-full flex flex-col justify-center items-center gap-4">
          {/* Preview State */}
          <div className={`relative group ${previewImageClass ? 'w-full h-full' : ''}`}>
            <img
              src={logoFileImage}
              alt="logo"
              className={previewImageClass || "max-h-[160px] rounded-[12px] object-contain"}
            />
            <button
              type="button"
              className={`absolute top-[-12px] right-[-12px] w-[32px] h-[32px] rounded-full flex items-center justify-center border border-white shadow-md transition-colors ${removeBtnType === 'blue' ? 'bg-[#00ADEF] hover:bg-[#0095d1]' : 'bg-[#D9D9D9] hover:bg-gray-300'
                }`}
              onClick={(e) => {
                e.stopPropagation();
                clearLogoImage();
              }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" style={removeBtnType === 'blue' ? {} : { filter: "invert(52%) sepia(71%) saturate(3082%) hue-rotate(167deg) brightness(100%) contrast(101%)" }}>
                <path d="M11 1L1 11M1 1L11 11" stroke={removeBtnType === 'blue' ? "white" : "black"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
          {showChangeText && (
            <span className="text-[#00ADEF] text-[14px] font-[500] cursor-pointer hover:underline" onClick={clearLogoImage}>
              Change Thumbnail
            </span>
          )}
        </div>
      )}
      <div className="ms-1 mt-1">
        <span className="text-red-600 sm:text-[10px] md:text-[12px] font-[600] text-start">
          {error && touched ? error : null}
        </span>
      </div>
    </div>
  );
};

export default UploadDocument;
